import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { Button, Card } from '@/components/ui';
import { useTranscriptionContext } from '@/contexts/TranscriptionContext';
import { useSession } from '@/contexts/SessionContext';
import { patchSessionTranscript } from '@/lib/api-client';
import { SPEAKER_ROLES, type SpeakerRole, type SpeakerRoleMap, type Turn } from '@/lib/types';
import { cn } from '@/lib/cn';

export function TranscriptPanel() {
  const {
    transcript,
    interim,
    isListening,
    supported,
    error,
    reset,
    engine,
    degraded,
    costUsd,
    audioSeconds,
    turns,
    speakerRoles,
    setSpeakerRole,
    reconnecting,
    stalled,
  } = useTranscriptionContext();
  const { sessionId } = useSession();

  if (!supported) {
    return <UnsupportedBrowser />;
  }

  function handleClear(): void {
    reset('');
    // Eagerly flush the empty transcript so a reload immediately after
    // Clear shows an empty panel rather than the pre-clear text. The 5s
    // periodic sync would do this anyway; this makes it instant.
    if (sessionId) {
      patchSessionTranscript(sessionId, '').catch((err: unknown) => {
        console.warn('clear transcript save failed:', err);
      });
    }
  }

  return (
    <Card variant="panel" padding="none" className="flex h-full min-h-[420px] flex-col">
      <Header
        isListening={isListening}
        hasTranscript={transcript.length > 0}
        onClear={handleClear}
        engine={engine}
        degraded={degraded}
        costUsd={costUsd}
        audioSeconds={audioSeconds}
        reconnecting={reconnecting}
      />
      {turns.length > 0 ? (
        <TurnsBody
          turns={turns}
          interim={interim}
          speakerRoles={speakerRoles}
          onSetRole={setSpeakerRole}
        />
      ) : (
        <TranscriptBody transcript={transcript} interim={interim} isListening={isListening} />
      )}
      {stalled && !reconnecting ? <StallRow /> : null}
      {error ? <ErrorRow message={error} /> : null}
    </Card>
  );
}

interface HeaderProps {
  isListening: boolean;
  hasTranscript: boolean;
  onClear: () => void;
  engine: 'deepgram' | 'webspeech';
  degraded: boolean;
  costUsd: number;
  audioSeconds: number;
  reconnecting: boolean;
}

function Header({
  isListening,
  hasTranscript,
  onClear,
  engine,
  degraded,
  costUsd,
  audioSeconds,
  reconnecting,
}: HeaderProps) {
  const [confirming, setConfirming] = useState(false);

  function handleClear() {
    if (!confirming) {
      setConfirming(true);
      window.setTimeout(() => setConfirming(false), 3000);
      return;
    }
    onClear();
    setConfirming(false);
  }

  return (
    <div className="flex items-center justify-between border-b border-stone-800/80 px-4 py-3">
      <div className="flex items-center gap-2">
        {reconnecting ? (
          <>
            <span className="relative inline-flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-500 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-500" />
            </span>
            <p className="text-[10px] uppercase tracking-[0.2em] text-amber-400">Reconnecting</p>
          </>
        ) : isListening ? (
          <>
            <span className="relative inline-flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
            </span>
            <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500">Recording</p>
          </>
        ) : (
          <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500">Live transcript</p>
        )}
        <EngineBadge engine={engine} degraded={degraded} costUsd={costUsd} audioSeconds={audioSeconds} />
      </div>
      {hasTranscript ? (
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<Trash2 className="h-3.5 w-3.5" />}
          onClick={handleClear}
          aria-label="Clear transcript"
        >
          {confirming ? 'Confirm clear' : 'Clear'}
        </Button>
      ) : null}
    </div>
  );
}

// --- speaker turns (P6 S1) ---------------------------------------------------

const ROLE_LABELS: Record<SpeakerRole, string> = {
  ME: 'Me',
  COUNSEL: 'Counsel',
  BENCH: 'Bench',
  WITNESS: 'Witness',
  OTHER: 'Other',
};

function roleLabel(speaker: number, roles: SpeakerRoleMap): string {
  const role = roles[String(speaker)];
  if (role) {
    return ROLE_LABELS[role];
  }
  return speaker < 0 ? 'Speaker' : `Speaker ${speaker}`;
}

// Per-speaker visual distinction within the stone/amber palette only. Colour is
// always paired with the role text, never the sole signal.
function chipClass(speaker: number, roles: SpeakerRoleMap): string {
  switch (roles[String(speaker)]) {
    case 'ME':
      return 'border-amber-700/50 bg-amber-950/30 text-amber-400';
    case 'COUNSEL':
      return 'border-stone-500 bg-stone-800/70 text-stone-100';
    case 'BENCH':
      return 'border-stone-600 bg-stone-800/40 text-stone-200';
    case 'WITNESS':
      return 'border-stone-700 bg-stone-900 text-stone-300';
    default:
      return 'border-stone-800 bg-stone-900 text-stone-500';
  }
}

// A tappable speaker label: a styled native select so one tap assigns a role
// (robust on desktop and touch, no popover plumbing). Tagging a speaker updates
// every turn by that speaker (roles are keyed by speaker index).
function SpeakerChip({
  speaker,
  roles,
  onSetRole,
}: {
  speaker: number;
  roles: SpeakerRoleMap;
  onSetRole: (speaker: number, role: SpeakerRole) => void;
}) {
  const current = roles[String(speaker)] ?? '';
  return (
    <select
      value={current}
      onChange={(e) => {
        if (e.target.value) {
          onSetRole(speaker, e.target.value as SpeakerRole);
        }
      }}
      title="Tap to set this speaker's role"
      aria-label={`Role for ${roleLabel(speaker, roles)}`}
      className={cn(
        'shrink-0 cursor-pointer appearance-none rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-[0.12em] focus:outline-none focus:ring-1 focus:ring-amber-700/60',
        chipClass(speaker, roles),
      )}
    >
      <option value="" disabled>
        {roleLabel(speaker, roles)}
      </option>
      {SPEAKER_ROLES.map((r) => (
        <option key={r} value={r}>
          {ROLE_LABELS[r]}
        </option>
      ))}
    </select>
  );
}

function TurnsBody({
  turns,
  interim,
  speakerRoles,
  onSetRole,
}: {
  turns: Turn[];
  interim: string;
  speakerRoles: SpeakerRoleMap;
  onSetRole: (speaker: number, role: SpeakerRole) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom < 140) {
      el.scrollTop = el.scrollHeight;
    }
  }, [turns, interim]);

  return (
    <div ref={scrollRef} className="flex-1 space-y-2.5 overflow-y-auto px-4 py-4">
      {turns.map((turn, i) => (
        <div
          key={i}
          className={cn(
            'border-l-2 pl-2.5',
            speakerRoles[String(turn.speaker)] === 'ME'
              ? 'border-amber-700/40'
              : 'border-stone-700/40',
          )}
        >
          <SpeakerChip speaker={turn.speaker} roles={speakerRoles} onSetRole={onSetRole} />
          <p className="mt-1 whitespace-pre-wrap font-mono text-[15px] leading-[1.6] text-stone-200">
            {turn.text}
          </p>
        </div>
      ))}
      {interim ? (
        <p className="whitespace-pre-wrap pl-2.5 font-mono text-[15px] italic leading-[1.6] text-stone-500">
          {interim}
        </p>
      ) : null}
    </div>
  );
}

interface TranscriptBodyProps {
  transcript: string;
  interim: string;
  isListening: boolean;
}

function TranscriptBody({ transcript, interim, isListening }: TranscriptBodyProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto scroll to bottom when new text arrives so the operator always sees
  // the latest line. Use a small threshold so the operator can still scroll
  // up to read earlier content without being yanked back.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom < 120) {
      el.scrollTop = el.scrollHeight;
    }
  }, [transcript, interim]);

  if (!transcript && !interim) {
    return (
      <div className="flex flex-1 items-center justify-center p-6">
        <p className="max-w-sm text-center font-mono text-sm leading-[1.7] text-stone-600">
          {isListening
            ? 'Listening. Start speaking to see the transcript appear here.'
            : 'Press start listening to begin capturing the hearing. The transcript appears here in real time and persists to this session.'}
        </p>
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      className="flex-1 overflow-y-auto px-4 py-4 font-mono text-[15px] leading-[1.7]"
    >
      {transcript ? <span className="whitespace-pre-wrap text-stone-200">{transcript}</span> : null}
      {interim ? (
        <>
          {transcript ? ' ' : null}
          <span className="italic text-stone-500">{interim}</span>
        </>
      ) : null}
    </div>
  );
}

// Shows which transcription engine is in effect and, on Deepgram, the running
// cost (Section 1.7 honest cost). On fallback it makes the downgrade visible
// (Section 1.6) so the operator knows accuracy has dropped to Web Speech.
function EngineBadge({
  engine,
  degraded,
  costUsd,
  audioSeconds,
}: {
  engine: 'deepgram' | 'webspeech';
  degraded: boolean;
  costUsd: number;
  audioSeconds: number;
}) {
  if (engine === 'deepgram') {
    const minutes = audioSeconds / 60;
    return (
      <span
        className="inline-flex items-center gap-1 rounded border border-amber-900/40 bg-amber-950/20 px-1.5 py-0.5 text-[10px] uppercase tracking-[0.15em] text-amber-400"
        title="High-accuracy transcription via Deepgram Nova-3"
      >
        Deepgram
        {audioSeconds > 0 ? (
          <span className="font-mono text-amber-300/80">
            ${costUsd.toFixed(3)} · {minutes.toFixed(1)}m
          </span>
        ) : null}
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 rounded border border-stone-700/60 px-1.5 py-0.5 text-[10px] uppercase tracking-[0.15em] text-stone-500"
      title={
        degraded
          ? 'Deepgram dropped out; using the free in-browser Web Speech API'
          : 'Free in-browser Web Speech API'
      }
    >
      {degraded ? 'Web Speech · fallback' : 'Web Speech'}
    </span>
  );
}

// Quiet stall warning (courtroom-ready sprint): listening, but no text for 30s.
// Amber and calm — a nudge to glance at the mic, not an alarm.
function StallRow() {
  return (
    <div className="flex items-center gap-2 border-t border-amber-900/30 bg-amber-950/10 px-4 py-2 text-xs text-amber-300/90">
      <AlertTriangle className="h-3.5 w-3.5 text-amber-400" aria-hidden />
      <span>No speech detected for 30 seconds. Still listening — check the microphone if the room is not actually silent.</span>
    </div>
  );
}

function ErrorRow({ message }: { message: string }) {
  return (
    <div className="flex items-center gap-2 border-t border-red-900/30 bg-red-950/10 px-4 py-2 text-xs text-red-300">
      <AlertTriangle className="h-3.5 w-3.5 text-red-400" aria-hidden />
      <span>Speech recognition: {message}</span>
    </div>
  );
}

function UnsupportedBrowser() {
  return (
    <Card
      variant="panel"
      padding="lg"
      className="flex h-full min-h-[420px] flex-col items-center justify-center text-center"
    >
      <AlertTriangle className="mb-3 h-6 w-6 text-amber-500" aria-hidden />
      <p className="font-display text-xl italic text-stone-100">Browser not supported</p>
      <p className="mt-2 max-w-md text-sm text-stone-400">
        Live transcription needs the Web Speech API, which currently runs only in Chrome and Edge.
        Open Court Copilot in Chrome or Edge to capture courtroom audio in real time.
      </p>
    </Card>
  );
}
